import { Subscription } from '../lib/supabase';
import { formatCurrency } from './currency';

export interface EmailData {
  subscription: Subscription;
  daysUntilRenewal: number;
  userEmail: string;
  userName?: string;
}

// HTML Email Template
export const getRenewalReminderHTML = (data: EmailData): string => {
  const { subscription, daysUntilRenewal, userEmail, userName } = data;
  const renewalDate = new Date(subscription.next_renewal_date);
  
  return `
  <!DOCTYPE html>
  <html>
  <head>
    <meta charset="utf-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Subscription Renewal Reminder</title>
    <style>
      body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; margin: 0; padding: 0; }
      .container { max-width: 600px; margin: 0 auto; padding: 20px; }
      .header { background: linear-gradient(135deg, #32CD32, #20B2AA); color: white; padding: 30px; text-align: center; border-radius: 10px 10px 0 0; }
      .content { background: #f9f9f9; padding: 30px; border-radius: 0 0 10px 10px; }
      .subscription-card { background: white; border: 2px solid #32CD32; border-radius: 8px; padding: 20px; margin: 20px 0; }
      .amount { font-size: 24px; font-weight: bold; color: #32CD32; margin: 10px 0; }
      .date { color: #666; font-size: 14px; }
      .button { display: inline-block; background: #32CD32; color: white; padding: 12px 24px; text-decoration: none; border-radius: 5px; margin: 20px 0; }
      .footer { text-align: center; color: #666; font-size: 12px; margin-top: 30px; }
      .unsubscribe { color: #999; font-size: 11px; text-align: center; margin-top: 20px; }
    </style>
  </head>
  <body>
    <div class="container">
      <div class="header">
        <h1>🔔 Subscription Renewal Reminder</h1>
        <p>Your subscription is renewing soon!</p>
      </div>
      
      <div class="content">
        <p>Hi ${userName || 'there'},</p>
        
        <p>This is a friendly reminder that your subscription will renew in <strong>${daysUntilRenewal} day${daysUntilRenewal === 1 ? '' : 's'}</strong>.</p>
        
        <div class="subscription-card">
          <h2>${subscription.name}</h2>
          <div class="amount">${formatCurrency(subscription.cost, subscription.currency || 'USD')}</div>
          <div class="date">Renews on ${renewalDate.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</div>
          ${subscription.payment_method ? `<p><strong>Payment Method:</strong> ${subscription.payment_method}</p>` : ''}
          ${subscription.notes ? `<p><strong>Notes:</strong> ${subscription.notes}</p>` : ''}
        </div>
        
        <a href="${process.env.REACT_APP_SITE_URL || 'https://your-app.netlify.app'}/dashboard" class="button">View All Subscriptions</a>
        
        <div class="footer">
          <p>This email was sent to ${userEmail}</p>
          <p>SubLime - Smart Subscription Management</p>
        </div>
        
        <div class="unsubscribe">
          <p>If you no longer wish to receive these reminders, please update your notification settings in the app.</p>
        </div>
      </div>
    </div>
  </body>
  </html>
  `;
};

export const getRenewalReminderText = (data: EmailData): string => {
  const { subscription, daysUntilRenewal, userName } = data;
  const renewalDate = new Date(subscription.next_renewal_date);
  
  return `
Subscription Renewal Reminder

Hi ${userName || 'there'},

This is a friendly reminder that your subscription will renew in ${daysUntilRenewal} day${daysUntilRenewal === 1 ? '' : 's'}.

Subscription: ${subscription.name}
Amount: ${formatCurrency(subscription.cost, subscription.currency || 'USD')}
Renewal Date: ${renewalDate.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
${subscription.payment_method ? `Payment Method: ${subscription.payment_method}` : ''}
${subscription.notes ? `Notes: ${subscription.notes}` : ''}

View all your subscriptions: ${process.env.REACT_APP_SITE_URL || 'https://your-app.netlify.app'}/dashboard

Best regards,
SubLime Team

---
This is an automated reminder from SubLime. To manage your notification preferences, please visit the app settings.
  `.trim();
};

// Multiple subscriptions reminder template
export const getMultipleRenewalsHTML = (subscriptions: Subscription[], daysUntilRenewal: number, userEmail: string, userName?: string): string => {
  
  const subscriptionList = subscriptions.map(sub => `
    <div class="subscription-card">
        <h3>${sub.name}</h3>
        <div class="amount">${formatCurrency(sub.cost, sub.currency || 'USD')}</div>
        <div class="date">Renews on ${new Date(sub.next_renewal_date).toLocaleDateString('en-US', { 
            weekday: 'long', 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
        })}</div>
    </div>
  `).join('');

  return `
<!DOCTYPE html>
<html>
<head>
    <meta charset="utf-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Multiple Subscription Renewals</title>
    <style>
        body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; }
        .container { max-width: 600px; margin: 0 auto; padding: 20px; }
        .header { background: linear-gradient(135deg, #32CD32, #20B2AA); color: white; padding: 30px; text-align: center; border-radius: 10px 10px 0 0; }
        .content { background: #f9f9f9; padding: 30px; border-radius: 0 0 10px 10px; }
        .subscription-card { background: white; border: 1px solid #ddd; border-radius: 8px; padding: 20px; margin: 10px 0; }
        .amount { font-size: 20px; font-weight: bold; color: #32CD32; }
        .date { color: #666; font-size: 14px; }
        .button { display: inline-block; background: #32CD32; color: white; padding: 12px 24px; text-decoration: none; border-radius: 5px; margin: 10px 0; }
        .footer { text-align: center; margin-top: 30px; color: #666; font-size: 12px; }
        .warning { background: #fff3cd; border: 1px solid #ffeaa7; border-radius: 5px; padding: 15px; margin: 15px 0; }
    </style>
</head>
<body>
    <div class="container">
        <div class="header">
            <h1>🔔 Multiple Subscription Renewals</h1>
            <p>You have ${subscriptions.length} subscription${subscriptions.length === 1 ? '' : 's'} renewing soon!</p>
        </div>
        
        <div class="content">
            <p>Hi ${userName || 'there'},</p>
            
            <p>You have <strong>${subscriptions.length} subscription${subscriptions.length === 1 ? '' : 's'}</strong> that will renew in <strong>${daysUntilRenewal} day${daysUntilRenewal === 1 ? '' : 's'}</strong>.</p>
            
            ${subscriptionList}
            
            <div class="warning">
                <strong>💡 Tip:</strong> Review these subscriptions and make sure your payment methods are up to date.
            </div>
            
            <p>
                <a href="${process.env.REACT_APP_SITE_URL || 'https://your-app.netlify.app'}/dashboard" class="button">
                    View All Subscriptions
                </a>
            </p>
        </div>
        
        <div class="footer">
            <p>This email was sent from SubLime - Your Smart Subscription Manager</p>
            <p>You can manage your notification preferences in your account settings.</p>
        </div>
    </div>
</body>
</html>
  `;
}; 